import {useEffect, useLayoutEffect, useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'

import {loadPackageTranslations, type TranslationModule} from './loadPackageTranslations'

type FeatureTranslationsLoaderProps = {
  /**
   * Translation modules to load.
   * These should be module-level constants to avoid re-loading on every render.
   */
  translations: TranslationModule[]
  /**
   * Fallback UI to show while translations are loading. Defaults to `null`.
   */
  fallback?: ReactNode
  children: ReactNode
}

/**
 * Wraps feature content and ensures its translations are loaded before rendering.
 *
 * Place this at the feature's route level so translations are loaded lazily
 * when the feature is actually navigated to, rather than eagerly at app startup.
 *
 * Only loads translations for the current language and i18n fallback language.
 * When the user switches language, the new translations are loaded on demand.
 *
 * @example
 * ```tsx
 * import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
 * import {myFeatureTranslations} from '../localization'
 *
 * const MyFeatureRouteWrapper = () => (
 *   <FeatureTranslationsLoader translations={myFeatureTranslations}>
 *     <MyFeaturePage />
 *   </FeatureTranslationsLoader>
 * )
 * ```
 */
export const FeatureTranslationsLoader = ({
  translations,
  fallback = null,
  children
}: FeatureTranslationsLoaderProps) => {
  const {i18n} = useTranslation()
  const [isLoaded, setIsLoaded] = useState(false)

  useLayoutEffect(() => {
    let cancelled = false

    void loadPackageTranslations(i18n, translations).then(() => {
      if (!cancelled) {
        setIsLoaded(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [i18n, translations])

  useEffect(() => {
    const handler = () => {
      void loadPackageTranslations(i18n, translations)
    }

    i18n.on('languageChanged', handler)
    return () => {
      i18n.off('languageChanged', handler)
    }
  }, [i18n, translations])

  if (!isLoaded) {
    return <>{fallback}</>
  }

  return <>{children}</>
}
